import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/api'
import { isNative } from '@/services/platform'
import { tokenStore } from '@/services/tokenStore'

/**
 * notifications — generisches In-App-Postfach (Glocke) + Browser-Benachrichtigungen
 * für ALLE Alarmtypen des Frameworks (Release 2.8.0).
 *
 * Quelle ist GET /notifications: der Server hat bereits nach Abo + Min-Severity +
 * typ-spezifischen Filtern gefiltert (eine Zeile = ein zugestellter Alarm), daher
 * KEIN Client-seitiges Severity-Filtern mehr nötig (anders als der ältere,
 * partner-spezifische alarmNotifications-Poller, der weiter parallel läuft).
 *
 * Mechanik (bewusst leichtgewichtig, KEINE Service-Worker/VAPID):
 *   • Poll alle 30s: GET /notifications?since=<lastSeen> → pro neuem Eintrag eine
 *     Web-Notification (nur Browser, nicht native — native iOS nutzt APNs) + einen
 *     In-App-Toast über das bestehende 'secsu:push-notification'-CustomEvent.
 *   • Das Postfach (Liste + Unread-Badge) funktioniert überall (auch native).
 */

const LS_LASTSEEN = 'secsu-notify-lastseen'
const POLL_MS = 30_000
const SUMMARY_THRESHOLD = 3

export const useNotificationsStore = defineStore('notifications', () => {
  const items       = ref([])        // letzte Inbox-Einträge (für die Glocke)
  const unreadCount = ref(0)
  const loading     = ref(false)
  const lastSeenAt  = ref(localStorage.getItem(LS_LASTSEEN) || null)
  const permission  = ref(typeof Notification !== 'undefined' ? Notification.permission : 'denied')

  const supported = computed(() => typeof window !== 'undefined' && 'Notification' in window && !isNative)

  let timer = null
  let polling = false
  let routerRef = null
  let tFn = (key) => key
  function setTranslator(t) { if (typeof t === 'function') tFn = t }

  function setLastSeen(v) {
    lastSeenAt.value = v
    if (v) localStorage.setItem(LS_LASTSEEN, v)
  }

  async function requestPermission() {
    if (!supported.value) return permission.value
    try { permission.value = await Notification.requestPermission() } catch { /* ignore */ }
    return permission.value
  }

  // Eine Web-Notification (+ In-App-Toast) abfeuern.
  function fire({ title, body, link, tag }) {
    if (supported.value && permission.value === 'granted') {
      try {
        const n = new Notification(title, { body, tag, icon: '/logo.png' })
        n.onclick = () => {
          try { window.focus() } catch { /* ignore */ }
          if (link && routerRef) routerRef.push(link).catch(() => {})
          n.close()
        }
      } catch { /* ignore */ }
    }
    try {
      window.dispatchEvent(new CustomEvent('secsu:push-notification', {
        detail: { title, body, data: { route: link } },
      }))
    } catch { /* ignore */ }
  }

  function sendTest() {
    fire({ title: 'secDASH', body: tFn('notifications.testBody'), tag: 'secsu-notify-test' })
  }

  // Glocke: die letzten N Einträge + Unread-Badge laden (kein Notification-Firing).
  async function fetchInbox(limit = 20) {
    if (!tokenStore.getSync()) return
    loading.value = true
    try {
      const { data } = await api.getNotifications({ limit })
      items.value = Array.isArray(data?.data) ? data.data : []
      if (typeof data?.unread_total === 'number') unreadCount.value = data.unread_total
    } catch { /* still show whatever we have */ } finally {
      loading.value = false
    }
  }

  async function fetchUnread() {
    if (!tokenStore.getSync()) return
    try {
      const { data } = await api.getNotificationsUnread()
      if (typeof data?.count === 'number') unreadCount.value = data.count
    } catch { /* ignore */ }
  }

  async function markRead(id) {
    try {
      await api.markNotificationRead(id)
      const it = items.value.find(x => x.id === id)
      if (it && !it.read_at) { it.read_at = new Date().toISOString(); unreadCount.value = Math.max(0, unreadCount.value - 1) }
    } catch { /* ignore */ }
  }

  async function markAllRead() {
    try {
      await api.markAllNotificationsRead()
      const now = new Date().toISOString()
      items.value.forEach(x => { if (!x.read_at) x.read_at = now })
      unreadCount.value = 0
    } catch { /* ignore */ }
  }

  // Poll-Tick: neue Einträge seit lastSeen → Notifications feuern.
  async function tick() {
    if (polling || !tokenStore.getSync()) return
    polling = true
    try {
      const params = { limit: 50 }
      if (lastSeenAt.value) params.since = lastSeenAt.value
      const { data } = await api.getNotifications(params)
      const fresh = Array.isArray(data?.data) ? data.data : []
      if (typeof data?.unread_total === 'number') unreadCount.value = data.unread_total

      if (fresh.length) {
        // lastSeen über alle neuen Einträge vorrücken (neueste zuerst → [0]).
        const newest = fresh[0]?.created_at
        if (newest) setLastSeen(newest)

        if (fresh.length > SUMMARY_THRESHOLD) {
          fire({
            title: tFn('notifications.bellTitle'),
            body:  tFn('notifications.unreadMany', { count: fresh.length }),
            link:  '/user/settings/notifications',
            tag:   'secsu-notify-summary',
          })
        } else {
          for (const e of fresh) {
            fire({
              title: e.title || tFn('notifications.bellTitle'),
              body:  e.body || '',
              link:  e.link || '/user/settings/notifications',
              tag:   `secsu-notify-${e.id}`,
            })
          }
        }
        // Glocke aktualisieren (neueste oben anfügen, Dedupe per id).
        const known = new Set(items.value.map(x => x.id))
        items.value = [...fresh.filter(x => !known.has(x.id)), ...items.value].slice(0, 30)
      }
    } catch { /* poll next interval */ } finally {
      polling = false
    }
  }

  function start(router = null, t = null) {
    if (router) routerRef = router
    if (t) setTranslator(t)
    if (!lastSeenAt.value) setLastSeen(new Date().toISOString())
    fetchInbox()
    if (timer) return
    timer = setInterval(tick, POLL_MS)
  }

  function stop() {
    if (timer) { clearInterval(timer); timer = null }
  }

  return {
    items, unreadCount, loading, permission, supported,
    requestPermission, sendTest, setTranslator,
    fetchInbox, fetchUnread, markRead, markAllRead,
    start, stop,
  }
})
